import { Button } from '@starter/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Row } from '../account/row.tsx';
import { useCreditHistory, useCredits } from '../credits/api.ts';
import { Paywall } from '../credits/paywall.tsx';

/**
 * `/account/subscription` — the plan, read-only and honest about why.
 *
 * The original's rows are here: the plan, what it renews at, the way to change
 * it and the way to cancel. What is not here is a way to pay, and that is the
 * owner's boundary rather than an omission — this rebuild goes up to the
 * paywall and stops (`prd/DIVERGENCE.md`). So the screen says the two true
 * things: every account is on the free tier, and there is nothing to buy.
 *
 * The credits are here now: the balance, where it came from, and the last
 * dozen movements. A number nobody can explain is a number people distrust,
 * which is why the ledger has rows rather than a total (`domain/credits.ts`).
 */
function SubscriptionPage() {
  return (
    <div className="flex flex-col" data-testid="subscription-section">
      <Row
        label="Plan"
        sub="What you are on, and what it would renew at."
        testId="subscription-plan"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink" data-testid="subscription-plan-line">
            No active plan · you are on the free tier.
          </p>
          <p className="text-sm text-ink-muted">
            Everything on this build is free and stays free: it is a rebuild of vendy up to the
            paywall, and the paywall is where it stops.
          </p>
          <div>
            <Paywall
              trigger={
                <Button data-testid="subscription-choose" variant="secondary">
                  Choose a plan
                </Button>
              }
            />
          </div>
          <p className="text-3xs leading-4 text-ink-muted" data-testid="subscription-no-payment">
            The dialog behind that button is the measured one, with the real prices. What it will
            not do is take a payment — nothing here reaches a card.
          </p>
        </div>
      </Row>

      <CreditsRow />
      <HistoryRow />

      <Row label="Invoices" sub="Receipts, once there is something to receipt.">
        <p className="text-sm text-ink-muted" data-testid="subscription-invoices">
          None, and none are coming while nothing is charged.
        </p>
      </Row>
    </div>
  );
}

/** What is left, and what has ever arrived or gone. */
function CreditsRow() {
  const credits = useCredits();
  const balance = credits.data?.credits;

  return (
    <Row
      label="Credits"
      sub="What is left to spend, and where it came from."
      testId="subscription-credits"
    >
      <div className="flex flex-col gap-3">
        <p
          className="text-[20px] leading-6 font-bold text-ink"
          data-testid="subscription-credits-line"
        >
          {balance === undefined ? '—' : `${balance.available} credits`}
        </p>
        <p className="text-sm text-ink-muted">
          {balance === undefined
            ? 'Reading the ledger…'
            : `${balance.granted} granted, ${balance.spent} spent. Credits leave the balance when a work is delivered — never when one fails.`}
        </p>
        {balance !== undefined && balance.committed > 0 ? (
          <p className="text-sm text-ink-muted" data-testid="subscription-in-flight">
            {`${balance.committed} promised to works that are still running, so ${balance.spendable} can be spent right now.`}
          </p>
        ) : null}
        <div>
          <Button render={<Link to="/generations" />} variant="secondary">
            See what you have made
          </Button>
        </div>
      </div>
    </Row>
  );
}

/**
 * The history, which is why the balance is a ledger and not a column: a number
 * nobody can explain is a number people distrust.
 */
function HistoryRow() {
  const history = useCreditHistory(12);
  const entries = history.data?.entries ?? [];

  return (
    <Row
      label="History"
      sub="The last dozen movements, newest first."
      testId="subscription-history"
    >
      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">Nothing yet.</p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="credits-history">
          {entries.map((entry) => (
            <li className="flex items-baseline justify-between gap-4 text-sm" key={entry.id}>
              <span className="min-w-0 flex-1 truncate text-ink">{entry.reason}</span>
              <span className={entry.amount < 0 ? 'text-ink-muted' : 'font-semibold text-ink'}>
                {entry.amount > 0 ? `+${entry.amount}` : entry.amount}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Row>
  );
}

export const Route = createFileRoute('/account/subscription')({
  component: SubscriptionPage,
});
